import { useId } from 'react';
import { cn } from '@/shared/lib';
import { FieldLabel } from '@/shared/ui/field-label';
import type { InputProps } from './types';
import { controlStyles, fieldStyles } from './styles';

export type { InputProps, InputSize } from './types';

/**
 * Builds the `aria-describedby` value for a field.
 *
 * Hint and error can both be present, and both belong in the description — a
 * validation message the user cannot hear is not an accessible error. Order is
 * fixed so the same field always announces in the same sequence.
 */
function describedBy(fieldId: string, hint?: string, error?: string): string | undefined {
  const ids = [hint ? `${fieldId}-hint` : null, error ? `${fieldId}-error` : null].filter(
    (id): id is string => id !== null,
  );
  return ids.length > 0 ? ids.join(' ') : undefined;
}

/**
 * Single-line text field with a bound label, optional hint, and optional error.
 *
 * Uncontrolled-friendly: pass `value`/`defaultValue` and `onChange` through
 * unchanged. A caller-supplied `id` is respected so a form can point an external
 * element (`aria-labelledby`, a datalist) at this control.
 */
export function Input({
  label,
  hideLabel = false,
  hint,
  error,
  required = false,
  optional = false,
  size = 'md',
  className,
  id,
  ...rest
}: InputProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  return (
    <div className={fieldStyles.root}>
      <FieldLabel
        htmlFor={fieldId}
        required={required}
        optional={optional}
        visuallyHidden={hideLabel}
      >
        {label}
      </FieldLabel>
      <input
        {...rest}
        id={fieldId}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(fieldId, hint, error)}
        className={cn(controlStyles({ size, invalid: Boolean(error) }), className)}
      />
      {hint ? (
        <p id={`${fieldId}-hint`} className={fieldStyles.hint}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${fieldId}-error`} className={fieldStyles.error}>
          {error}
        </p>
      ) : null}
    </div>
  );
}